import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'

import useTrans from '@/hooks/useTrans'

const PoolItemSkeleton = () => {
  const trans = useTrans()

  const theme = useTheme()
  const sm = useMediaQuery(theme.breakpoints.up('sm'))
  const md = useMediaQuery(theme.breakpoints.up('md'))

  return (
    <Grid item xs={md ? 4 : sm ? 6 : 12}>
      <Stack
        direction="row"
        alignItems="center"
        className="py-4 px-6 bg-[#1A2332] rounded-t-[16px]"
      >
        <Stack direction="row">
          <Skeleton height={40} width={40} variant="circular" />
          <Skeleton height={40} width={40} variant="circular" className="relative left-[-8px]" />
        </Stack>
        <Typography
          data-testid="txt-pool-pair-name"
          className="text-[20px] font-semibold text-[#C2CBD9]"
        >
          <Skeleton height={30} width={140} />
        </Typography>
      </Stack>
      <Stack className="bg-[#1E2735] p-6 rounded-b-[16px] gap-4">
        <Stack direction="row">
          <Stack flex="1" direction="row" alignItems="center" gap="12px">\
            <Box>
              <Typography data-testid="table-heading-pool-fee" className="text-sm text-[#9CA3AE]">
                {trans.home.commission}
              </Typography>
              <Skeleton height={28} width={55} />
            </Box>
          </Stack>

          <Stack flex="1" direction="row" alignItems="center" gap="12px">
            <Box>
              <Typography data-testid="table-heading-pool-fee" className="text-sm text-[#9CA3AE]">
                APR
              </Typography>
              <Skeleton height={28} width={55} />
            </Box>
          </Stack>
        </Stack>
        <Box className="py-4 border-t border-b border-solid border-[#3B4A5E]">
          <Table size="small" padding="none">
            <TableBody>
              <TableRow>
                <TableCell className="border-none py-1.5">
                  <Typography
                    data-testid="table-heading-pool-24h-volume-?"
                    sx={{
                      fontSize: 14,
                      color: '#888C9E',
                    }}
                  >
                    {trans.liquidity.pool.volume_24h}
                  </Typography>
                </TableCell>
                <TableCell className="border-none py-1.5 pl-2 text-right">
                  <Skeleton height={20} width={50} className="inline-block transform-none" />
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell className="border-none py-1.5">
                  <Typography
                    data-testid="table-heading-pool-24h-fee-?"
                    sx={{
                      fontSize: 14,
                      color: '#888C9E',
                    }}
                  >
                    {trans.liquidity.pool.fee_24h}
                  </Typography>
                </TableCell>
                <TableCell className="border-none py-2 pl-2 text-right">
                  <Skeleton height={20} width={50} className="inline-block transform-none" />
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell className="border-none py-2">
                  <Typography
                    data-testid="table-heading-pool-liquidity"
                    sx={{
                      fontSize: 14,
                      color: '#888C9E',
                    }}
                  >
                    {trans.liquidity.pool.total_liquidity}
                  </Typography>
                </TableCell>
                <TableCell className="border-none py-2 pl-2 text-right">
                  <Skeleton height={20} width={60} className="inline-block transform-none" />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
        <Stack gap="8px" alignItems="center">
          <Skeleton height={20} width="70%" className="transform-none" />
          <Skeleton height={40} width="100%" className="transform-none" />
        </Stack>
      </Stack>
    </Grid>
  )
}

const PoolSkeleton = ({ numPools = 0 }: { numPools?: number }) => {
  const theme = useTheme()
  const sm = useMediaQuery(theme.breakpoints.up('sm'))
  const md = useMediaQuery(theme.breakpoints.up('md'))

  // The number of pool item skeletons should fill the remaining blank space of
  // the last pool line, e.g., on large screens with three pools per line, two
  // skeletons should show in the position of the remaining pool items if there
  // is currently one pool alone on that line.
  // If the last pool line has no empty space left, show a new line with all
  // pool skeletons.
  const numPoolsPerLine = md ? 3 : sm ? 2 : 3
  const numSkeletons = numPoolsPerLine - (numPools % numPoolsPerLine)

  return (
    <>
      {[...Array(numSkeletons)].map((_, index) => (
        <PoolItemSkeleton key={index} />
      ))}
    </>
  )
}

export default PoolSkeleton
